import React from 'react'
import Button from '@material-ui/core/Button' // MUI 4 import
import PropTypes from 'prop-types'
import styled, { css } from 'styled-components'
import { useTheme, alpha } from '@material-ui/core/styles' // Import alpha helper for opacity
import {
  getBorderRadius,
  getButtonPadding,
  getColorVariant,
  getFontWeight,
} from 'src/components/core/functions' // Import getBorderRadius function

// Fixed truncateStyles CSS: replaced 'display: block' with 'display: flex'
const truncateStyles = css`
  .MuiButton-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: flex;
    justify-content: ${({ alignment }) =>
      alignment === 'vertical' ? 'flex-start' : 'center'};
    align-items: center;
  }
`

// Define the base styles for the button using styled-components
const BaseButton = styled(Button)`
  box-shadow: none;
  text-transform: none;
  font-family: 'Inter';
  font-weight: ${({ size }) => getFontWeight(size)};
  border-radius: ${({ size }) => getBorderRadius(size)};
  padding: ${({ size }) => getButtonPadding(size)};
  letter-spacing: 0.2px;
  white-space: nowrap; // Prevent text from wrapping to the next line
  width: ${({ fullWidth }) => (fullWidth ? '100%' : 'fit-content')};
  ${({ truncate }) => truncate && truncateStyles}

  // Add specific styles for vertical alignment
  ${({ alignment }) =>
    alignment === 'vertical' &&
    css`
      .MuiButton-label {
        display: flex;
        justify-content: flex-start;
      }
    `}
  
  &:hover {
    box-shadow: none;
  } ;
`

// Define variant-specific styles for contained buttons
const ContainedButton = styled(BaseButton)`
  color: ${({ theme, color, disabled }) =>
    disabled
      ? theme.palette.action.disabled
      : getColorVariant(theme, color).opposite};
  background-color: ${({ theme, color, disabled }) =>
    disabled
      ? theme.palette.action.disabledBackground
      : getColorVariant(theme, color).main};
  &:hover {
    background-color: ${({ theme, color, disabled }) =>
      disabled
        ? theme.palette.action.disabledBackground
        : getColorVariant(theme, color).main};
    box-shadow: ${({ disabled }) =>
      disabled ? 'none' : '0px 2px 4px 0px rgba(145, 158, 171, 0.32)'};
  }
`

// Define variant-specific styles for outlined buttons
const OutlinedButton = styled(BaseButton)`
  color: ${({ theme, color, disabled }) =>
    disabled
      ? theme.palette.action.disabled
      : getColorVariant(theme, color).main};
  background-color: white; /* Outlined buton için beyaz background */
  border: 1px solid
    ${({ theme, color, disabled }) =>
      disabled
        ? theme.palette.action.disabledBackground
        : getColorVariant(theme, color).main};
  &:hover {
    background-color: ${({ theme, color, disabled }) =>
      disabled ? 'white' : alpha(getColorVariant(theme, color).main, 0.1)};
    border: 1px solid
      ${({ theme, color, disabled }) =>
        disabled && theme.palette.action.disabledBackground};
    color: ${({ theme, color, disabled }) =>
      disabled
        ? theme.palette.action.disabled
        : getColorVariant(theme, color).main};
  }
`
// Define variant-specific styles for text buttons
const TextButton = styled(BaseButton)`
  color: ${({ theme, color, disabled }) =>
    disabled
      ? theme.palette.action.disabled
      : getColorVariant(theme, color).main};
  &:hover {
    background-color: ${({ theme, color, disabled }) =>
      disabled ? 'none' : alpha(getColorVariant(theme, color).main, 0.1)};
  }
`

const CoreButton = React.forwardRef(
  (
    {
      variant = 'contained',
      color = 'primary',
      size = 'medium',
      truncate = false,
      fullWidth = false,
      disabled = false,
      onClick,
      children,
      alignment,
      ...props
    },
    ref,
  ) => {
    const theme = useTheme() // Access the theme using useTheme hook

    // Select the button variant based on the `variant` prop
    const ButtonVariant =
      {
        contained: ContainedButton,
        outlined: OutlinedButton,
        text: TextButton,
      }[variant] || ContainedButton

    return (
      <ButtonVariant
        ref={ref}
        theme={theme}
        color={color}
        size={size}
        alignment={alignment} // Make sure alignment is passed here
        truncate={truncate}
        fullWidth={fullWidth}
        disabled={disabled}
        onClick={disabled ? undefined : onClick}
        {...props}
      >
        {children}
      </ButtonVariant>
    )
  },
)

// Prop validation
CoreButton.propTypes = {
  variant: PropTypes.oneOf(['contained', 'outlined', 'text']),
  color: PropTypes.oneOf([
    'primary',
    'secondary',
    'error',
    'disabled',
    'info',
    'success',
    'warning',
    'white',
    'dark',
    'gray',
    'inherit',
  ]),
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  fullWidth: PropTypes.bool,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  children: PropTypes.node.isRequired,
  alignment: PropTypes.oneOf(['vertical', 'horizontal']), // Added alignment prop validation
}

export default CoreButton
